import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  useTheme,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  PersonOutline as UserIcon,
} from '@mui/icons-material';
import { API_BASE_URL } from '../api';
import apiService from '../services/apiService';

export default function Users() {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    role_id: '',
    mentor_id: '',
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await apiService.get('/users');
      console.log('Users data:', data); // Debug log
      // Handle standardized envelope { status, message, data }
      const list = Array.isArray(data) ? data : (data?.data ?? []);
      setUsers(list);
    } catch (error) {
      console.error('Fetch error:', error); // Debug log
      if (error.message === 'Session expired') {
        // apiService already handled logout
        return;
      }
      setError('Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const roleNameById = (roleId) => {
    const r = roles.find((x) => String(x.id) === String(roleId));
    return r?.name ?? '';
  };

  const professionalDrivers = users.filter(
    (u) => u.role?.name === 'driver_pro'
  );

  const showMentorField = (roleId) =>
    ['driver_novice', 'user', 'driver'].includes(roleNameById(roleId));

  const fetchRoles = async () => {
    try {
      const data = await apiService.get('/roles');
      console.log('Roles data:', data); // Debug log
      const list = Array.isArray(data) ? data : (data?.data ?? []);
      setRoles(list);
    } catch (error) {
      console.error('Error fetching roles:', error);
      if (error.message === 'Session expired') {
        // apiService already handled logout
        return;
      }
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        password_confirmation: '',
        role_id: user.role_id || '',
        mentor_id: user.mentor_id != null ? String(user.mentor_id) : '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        role_id: '',
        mentor_id: '',
      });
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      role_id: '',
      mentor_id: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      return;
    }

    try {
      const url = editingUser 
        ? `${API_BASE_URL}users/${editingUser.id}`
        : `${API_BASE_URL}users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      
      const payload = { ...formData };
      if (!payload.password) {
        delete payload.password;
        delete payload.password_confirmation;
      }
      if (showMentorField(payload.role_id) && payload.mentor_id) {
        payload.mentor_id = parseInt(payload.mentor_id, 10);
      } else {
        delete payload.mentor_id;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccess(editingUser ? 'User updated successfully!' : 'User created successfully!');
        fetchUsers();
        setTimeout(() => handleCloseDialog(), 1500);
      } else {
        const data = await response.json();
        setError(data.message || 'Operation failed');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        setSuccess('User deleted successfully!');
        fetchUsers();
      } else {
        setError('Failed to delete user');
      }
    } catch (error) {
      setError('Error deleting user');
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find((r) => String(r.id) === String(roleId));
    return role ? role.name : 'Unknown';
  };

  const displayRoleName = (u) => u.role?.name ?? getRoleName(u.role_id);

  /** Етикет в таблицата: шофьорите са driver, не user. */
  const formatRoleLabel = (roleName) => {
    if (!roleName) return 'UNKNOWN';
    return roleName.replace(/_/g, ' ').toUpperCase();
  };

  const getRoleIcon = (roleName) => {
    switch (roleName) {
      case 'super_admin':
        return <AdminIcon sx={{ fontSize: 16 }} />;
      case 'admin':
        return <AdminIcon sx={{ fontSize: 16 }} />;
      case 'manager':
        return <ManagerIcon sx={{ fontSize: 16 }} />;
      case 'driver_pro':
      case 'driver_novice':
      case 'driver':
        return <UserIcon sx={{ fontSize: 16 }} />;
      default:
        return <UserIcon sx={{ fontSize: 16 }} />;
    }
  };

  const getRoleColor = (roleName) => {
    switch (roleName) {
      case 'super_admin':
        return '#dc2626';
      case 'admin':
        return '#2563eb';
      case 'manager':
        return '#7c3aed';
      case 'driver_pro':
        return '#0d9488';
      case 'driver_novice':
        return '#ea580c';
      case 'driver':
        return '#059669';
      default:
        return '#64748b';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
          Users Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
            },
          }}
        >
          Add New User
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Users Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Mentor</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 600,
                          }}
                        >
                          {user.name.charAt(0).toUpperCase()}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {user.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            ID: {user.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getRoleIcon(displayRoleName(user))}
                        label={formatRoleLabel(displayRoleName(user))}
                        sx={{
                          backgroundColor: getRoleColor(displayRoleName(user)) + '20',
                          color: getRoleColor(displayRoleName(user)),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {user.mentor?.name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {new Date(user.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(user)}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user.id)}
                            sx={{ color: theme.palette.error.main }}
                            disabled={user.role === 'super_admin'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role_id}
                  label="Role"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      role_id: e.target.value,
                      mentor_id: showMentorField(e.target.value)
                        ? formData.mentor_id
                        : '',
                    })
                  }
                  required
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name.replace(/_/g, ' ').toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {showMentorField(formData.role_id) && (
                <FormControl fullWidth>
                  <InputLabel>Mentor (driver pro)</InputLabel>
                  <Select
                    value={formData.mentor_id || ''}
                    label="Mentor (driver pro)"
                    onChange={(e) =>
                      setFormData({ ...formData, mentor_id: e.target.value })
                    }
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>No mentor</em>
                    </MenuItem>
                    {professionalDrivers
                      .filter((d) => !editingUser || d.id !== editingUser.id)
                      .map((d) => (
                        <MenuItem key={d.id} value={String(d.id)}>
                          {d.name} ({d.email})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                helperText={editingUser ? 'Leave blank to keep current password' : ''}
              />
              
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={formData.password_confirmation}
                onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                required={!editingUser}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
